import { useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { LayoutDashboard, CalendarDays, Ticket, BookOpen, ShieldAlert, ScanLine, ClipboardList, BadgeCheck,
  GraduationCap, FileBarChart, Users, LogOut, Menu, Shield } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useHotel } from "@/context/HotelContext";
import { ALL_ROLES, OPS, LEAD, ROLES, isGlobal } from "@/lib/constants";

export const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, roles: ALL_ROLES, id: "dashboard" },
  { to: "/turni", label: "Turni e Rota", icon: CalendarDays, roles: OPS, id: "turni" },
  { to: "/ticket", label: "Ticket & SLA", icon: Ticket, roles: ALL_ROLES, id: "ticket" },
  { to: "/giornale", label: "Giornale & Consegne", icon: BookOpen, roles: ALL_ROLES, id: "giornale" },
  { to: "/incidenti", label: "Incidenti & Near miss", icon: ShieldAlert, roles: ALL_ROLES, id: "incidenti" },
  { to: "/ronde", label: "Ronde QR", icon: ScanLine, roles: OPS, id: "ronde" },
  { to: "/registri", label: "Registri", icon: ClipboardList, roles: ALL_ROLES, id: "registri" },
  { to: "/qualita", label: "Qualità", icon: BadgeCheck, roles: ALL_ROLES, id: "qualita" },
  { to: "/personale", label: "Personale & Formazione", icon: GraduationCap, roles: LEAD, id: "personale" },
  { to: "/report", label: "Report mensili", icon: FileBarChart, roles: ["admin", "coordinatore", "referente_hotel"], id: "report" },
  { to: "/utenti", label: "Utenti", icon: Users, roles: ["admin"], id: "utenti" },
];

const NavList = ({ onNavigate }) => {
  const { user } = useAuth();
  return (
    <nav className="flex flex-col gap-0.5 px-3">
      {NAV.filter((n) => n.roles.includes(user.role)).map((n) => (
        <NavLink key={n.to} to={n.to} end={n.to === "/"} onClick={onNavigate} data-testid={`nav-${n.id}`}
          className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${isActive
            ? "bg-amber-50 text-amber-700 border border-amber-200" : "text-slate-500 hover:text-slate-900 hover:bg-slate-100 border border-transparent"}`}>
          <n.icon className="w-4 h-4" />{n.label}
        </NavLink>
      ))}
    </nav>
  );
};

const Brand = () => (
  <div className="px-6 py-6 flex items-center gap-3">
    <div className="w-9 h-9 rounded-lg bg-amber-600 grid place-items-center"><Shield className="w-5 h-5 text-white" /></div>
    <div>
      <div className="font-heading font-bold text-slate-900 leading-none">MSE Control</div>
      <div className="eyebrow mt-1 text-[10px]">Security · Hospitality</div>
    </div>
  </div>
);

const UserBox = () => {
  const { user, logout } = useAuth();
  return (
    <div className="p-4 border-t border-slate-200">
      <div className="text-sm text-slate-900 font-medium truncate" data-testid="current-user-name">{user.name}</div>
      <div className="text-xs text-slate-500 truncate mb-3" data-testid="current-user-role">{ROLES[user.role]?.label}</div>
      <Button variant="outline" size="sm" className="w-full gap-2 border-slate-300" onClick={logout} data-testid="logout-btn">
        <LogOut className="w-4 h-4" />Esci
      </Button>
    </div>
  );
};

const HotelSelector = () => {
  const { user } = useAuth();
  const { hotels, hotelId, setHotelId, hotelName } = useHotel();
  if (!isGlobal(user))
    return <div className="text-sm text-slate-700 font-medium" data-testid="hotel-fixed">{hotelName(hotelId)}</div>;
  return (
    <Select value={hotelId} onValueChange={setHotelId}>
      <SelectTrigger className="w-[220px] sm:w-[260px] bg-white border-slate-300" data-testid="hotel-selector-dropdown"><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="all" data-testid="hotel-option-all">Tutte le strutture</SelectItem>
        {hotels.map((h) => <SelectItem key={h.id} value={h.id} data-testid={`hotel-option-${h.id}`}>{h.name}</SelectItem>)}
      </SelectContent>
    </Select>
  );
};

export default function Layout() {
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const current = NAV.find((n) => (n.to === "/" ? loc.pathname === "/" : loc.pathname.startsWith(n.to)));
  return (
    <div className="flex h-screen overflow-hidden">
      <aside className="w-64 bg-white border-r border-slate-200 flex-shrink-0 hidden md:flex flex-col justify-between">
        <div className="overflow-y-auto"><Brand /><NavList /></div>
        <UserBox />
      </aside>
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 border-b border-slate-200 bg-white/90 backdrop-blur-md px-4 sm:px-6 flex items-center justify-between gap-3 sticky top-0 z-20">
          <div className="flex items-center gap-3 min-w-0">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="md:hidden" data-testid="mobile-menu-btn"><Menu className="w-5 h-5" /></Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 p-0 bg-white border-slate-200 flex flex-col justify-between">
                <div><Brand /><NavList onNavigate={() => setOpen(false)} /></div>
                <UserBox />
              </SheetContent>
            </Sheet>
            <span className="eyebrow hidden sm:block truncate">{current?.label}</span>
          </div>
          <HotelSelector />
        </header>
        <main className="flex-1 overflow-y-auto">
          <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-7xl mx-auto w-full"><Outlet /></div>
        </main>
      </div>
    </div>
  );
}
